/*
  Component: Product reviews
  Responsibility: flipping Okendo's reviews widget between this product and the whole store
  when the switch above it is used.

  Which scope the page opens on is not decided here — sections/kl-product-reviews.liquid
  decides it from the product's review count and renders the widget already scoped, so
  nothing moves on load and Okendo's own DOM never has to be read to find out.

  Each scope is drawn at most once and then kept. The first flip costs a draw; every flip
  after it is the two mounts changing places, which is what makes the switch feel instant.
  Drawing both up front would be faster still on that first flip, and is not done, for two
  reasons. Every product page would fetch and render the store's entire review list whether
  or not anyone asked for it. And Okendo appears to size a widget from the width it is drawn
  at — it ships a small-container layout, .oke-is-small — which would make one drawn inside
  a hidden element measure nothing; that second reason is read off its stylesheet rather
  than confirmed, so treat it as the weaker of the two.

  Two things below belong to Okendo and are exact on purpose:

  - <div data-oke-widget> is its mount point, and data-oke-reviews-product-id is what scopes
    that mount to one product. Leaving the attribute off is what asks for the store's
    reviews — which is what @okendo/shopify-hydrogen does when it is given no product, and
    is how the Hydrogen site draws this same switch.
  - window.okeWidgetApi.initWidget(element) draws one mount, and the oke-script-loaded event
    on document says the API has arrived. Both are from Okendo's
    reviews-widget-plus/js/okendo-reviews.js, which its theme app embed loads on every page.
*/

const KL_PRODUCT_REVIEWS_SELECTOR = '[data-kl-product-reviews]';
const KL_PRODUCT_REVIEWS_TAB_SELECTOR = '[data-kl-product-reviews-tab]';
const KL_PRODUCT_REVIEWS_WIDGET_SELECTOR = '[data-kl-product-reviews-widget]';
const KL_PRODUCT_REVIEWS_PRODUCT_TAB = 'product';
const KL_PRODUCT_REVIEWS_OKENDO_READY_EVENT = 'oke-script-loaded';

/*
  Pure: the whole difference between the two scopes, with no DOM in sight.
*/
function getWidgetAttributes(tabName, productScopeId) {
  if (tabName !== KL_PRODUCT_REVIEWS_PRODUCT_TAB || !productScopeId) {
    return { 'data-oke-widget': '' };
  }

  return { 'data-oke-widget': '', 'data-oke-reviews-product-id': productScopeId };
}

function initOkendoWidget(widget) {
  try {
    if (!window.okeWidgetApi) {
      document.addEventListener(
        KL_PRODUCT_REVIEWS_OKENDO_READY_EVENT,
        () => {
          initOkendoWidget(widget);
        },
        { once: true }
      );
      return;
    }

    if (!widget.isConnected) return;

    window.okeWidgetApi.initWidget(widget);
  } catch (error) {
    console.debug('kl-product-reviews: Okendo could not draw the reviews widget', { error });
  }
}

/*
  hidden rather than a class: it is the state of the mount, and it takes the put-away scope
  out of the accessibility tree along with the layout.
*/
function showWidget(widgets, widgetHolder, tabName, productScopeId) {
  widgets.forEach((widget, scopeName) => {
    widget.hidden = scopeName !== tabName;
  });

  if (widgets.has(tabName)) return;

  const widget = document.createElement('div');

  Object.entries(getWidgetAttributes(tabName, productScopeId)).forEach(([attributeName, attributeValue]) => {
    widget.setAttribute(attributeName, attributeValue);
  });

  widgetHolder.append(widget);
  widgets.set(tabName, widget);

  initOkendoWidget(widget);
}

function selectTab(tabs, widgets, widgetHolder, selectedTab, productScopeId) {
  if (selectedTab.getAttribute('aria-pressed') === 'true') return;

  tabs.forEach((tab) => {
    tab.setAttribute('aria-pressed', String(tab === selectedTab));
  });

  showWidget(widgets, widgetHolder, selectedTab.dataset.klProductReviewsTab, productScopeId);
}

function initProductReviews(element) {
  if (!element || element.dataset.klProductReviewsReady === 'true') return;

  const tabs = Array.from(element.querySelectorAll(KL_PRODUCT_REVIEWS_TAB_SELECTOR));
  const widgetHolder = element.querySelector(KL_PRODUCT_REVIEWS_WIDGET_SELECTOR);

  if (tabs.length === 0 || !widgetHolder) {
    console.debug('kl-product-reviews: the section has no switch or nowhere to draw the widget');
    return;
  }

  const openTab = tabs.find((tab) => tab.getAttribute('aria-pressed') === 'true');
  const openWidget = widgetHolder.firstElementChild;

  if (!openTab || !openWidget) {
    console.debug('kl-product-reviews: the section opened without a scope already drawn');
    return;
  }

  element.dataset.klProductReviewsReady = 'true';

  const productScopeId = element.dataset.klProductReviewsScope;
  const widgets = new Map([[openTab.dataset.klProductReviewsTab, openWidget]]);

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      selectTab(tabs, widgets, widgetHolder, tab, productScopeId);
    });
  });
}

document.querySelectorAll(KL_PRODUCT_REVIEWS_SELECTOR).forEach(initProductReviews);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(KL_PRODUCT_REVIEWS_SELECTOR).forEach(initProductReviews);
});
